import { Col } from 'antd';
import { useFormikContext } from 'formik';
import { isNil } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountUnit } from '../../../../../api/types';
import InputNumberField from '../../../../../components/fields/InputNumberField';
import SelectField from '../../../../../components/fields/SelectField';
import FormFields from '../../../../../themes/admin/Fields/FormFields';
import { useFinanceProductDetailContext } from '../../FinanceProductDetailContext';
import useFinanceProductOptions from '../../shared/useFinanceProductOptions';
import { type FinanceProductFormValues } from '../index';
import DefaultIntervalField from './DefaultIntervalField';

const colSpan = { lg: 8, xs: 24 };

const Loan = ({ disabled = false }: { disabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);

    const { bank } = useFinanceProductDetailContext();

    const { amountUnitOptions } = useFinanceProductOptions(bank?.module?.companyId);

    const { values } = useFormikContext<FinanceProductFormValues>();

    const { loan } = values;

    const { disableStep, disableUnit } = useMemo(() => {
        const minUnit = loan?.minUnit;
        const maxUnit = loan?.maxUnit;
        const stepUnit = loan?.stepUnit;
        const step = loan?.step;

        const common = !isNil(minUnit) && !isNil(maxUnit);

        return {
            disableStep: common && minUnit !== maxUnit && Number.isFinite(step),
            disableUnit:
                common &&
                (minUnit !== maxUnit || (minUnit === AmountUnit.Currency && maxUnit === AmountUnit.Currency)) &&
                !isNil(stepUnit),
        };
    }, [loan?.maxUnit, loan?.minUnit, loan?.step, loan?.stepUnit]);

    return (
        <>
            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('financeProductDetails:fields.loanMin', {
                        returnObjects: true,
                    })}
                    addonAfter={
                        <FormFields.SelectField
                            itemProps={{ noStyle: true }}
                            name="loan.minUnit"
                            options={amountUnitOptions}
                        />
                    }
                    disabled={disabled}
                    max={loan?.minUnit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                    min={0}
                    name="loan.min"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('financeProductDetails:fields.loanMax', {
                        returnObjects: true,
                    })}
                    addonAfter={
                        <FormFields.SelectField
                            itemProps={{ noStyle: true }}
                            name="loan.maxUnit"
                            options={amountUnitOptions}
                        />
                    }
                    disabled={disabled}
                    max={loan?.maxUnit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                    min={0}
                    name="loan.max"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputNumberField
                    {...t('financeProductDetails:fields.loanStep', {
                        returnObjects: true,
                    })}
                    addonAfter={
                        <SelectField
                            disabled={disableUnit}
                            itemProps={{ noStyle: true }}
                            name="loan.stepUnit"
                            options={amountUnitOptions}
                        />
                    }
                    disabled={disableStep || disabled}
                    max={loan?.stepUnit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                    min={0}
                    name="loan.step"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <DefaultIntervalField
                    {...t('financeProductDetails:fields.loanDefault', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    max={loan?.unit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                    min={0}
                    name="loan.default"
                    unitProps={{ name: 'loan.unit' }}
                />
            </Col>
        </>
    );
};

export default Loan;
